/**
 * Lesson Schema — JSDoc type definitions and validation for schema-driven lessons.
 *
 * Defines the full lesson JSON structure including steps, questions,
 * globe actions, narration, and scoring types.
 */

// ── Type Definitions ─────────────────────────────────────────

/**
 * @typedef {'navigate'|'choropleth'|'show_divisions'|'show_pois'|'terrain'|'overture_layer'} GlobeActionType
 */

/**
 * A globe action that maps to an intent routed through query-router.js.
 * @typedef {Object} GlobeAction
 * @property {string} type - Intent type (e.g., 'navigate', 'choropleth', 'show_divisions')
 * @property {string} [command] - Natural language command to parse via AI (alternative to structured intent)
 * @property {Object} [intent] - Pre-built intent object passed directly to router.route()
 * @property {number} [delay_ms] - Delay before executing (default 0)
 */

/**
 * @typedef {Object} Narration
 * @property {string} text - Text displayed in the panel
 * @property {string} [speak] - Text spoken via speech synthesis (defaults to text if omitted)
 * @property {'before_action'|'after_action'|'with_action'} [timing] - When to narrate relative to globe action (default 'before_action')
 */

/**
 * @typedef {'multiple_choice'|'numeric'|'open_response'|'true_false'|'fill_blank'|'map_click'} QuestionType
 */

/**
 * A question within a lesson step.
 * @typedef {Object} Question
 * @property {QuestionType} type - Question format
 * @property {string} prompt - The question text
 * @property {string[]} [choices] - Answer choices (for multiple_choice)
 * @property {string|number} [correct_answer] - Correct answer (string for MC/TF/fill, number for numeric)
 * @property {number} [tolerance] - Acceptable range for numeric answers (e.g., ±5)
 * @property {string} [grading_rubric] - AI grading rubric for open_response questions
 * @property {MapClickTarget} [target] - Target feature for map_click questions
 * @property {number} [attempts] - Max click attempts for map_click questions (default 3)
 * @property {string} explanation - Explanation shown after answering
 * @property {number} [points] - Point value (default 1)
 * @property {string} [hint] - Optional hint text
 */

/**
 * Target of a map_click question — what the student must click on the globe.
 * Matching is case- and diacritic-insensitive; countries also match by ISO3.
 * @typedef {Object} MapClickTarget
 * @property {'country'|'continent'} kind - Kind of feature to click
 * @property {string} name - Display name of the target (e.g., 'France', 'Africa')
 * @property {string} [iso] - Optional ISO3 code (countries only, e.g., 'FRA')
 */

/**
 * @typedef {'guided'|'freeform'|'quiz'|'narration'|'checkpoint'|'explore'|'remediation'} StepType
 *
 * 'remediation' (CL-149, roadmap H2.3): a step that is invisible to sequential
 * flow — default "next" traversal skips it — and reachable only as an explicit
 * on_pass/on_fail target. Otherwise a normal step (narration, globe_action,
 * question all allowed). Authoring convention: place immediately after the
 * step that branches to it, id prefixed "rem-".
 */

/**
 * A single step in a lesson.
 * @typedef {Object} Step
 * @property {string} id - Unique step identifier
 * @property {StepType} type - Step type
 * @property {string} title - Short step title
 * @property {Narration} [narration] - Text/speech content for this step
 * @property {GlobeAction} [globe_action] - Globe action to execute
 * @property {Question} [question] - Question to ask the student
 * @property {Object} [freeform] - Freeform exploration config
 * @property {string[]} [freeform.suggested_prompts] - Clickable prompt suggestions
 * @property {number} [freeform.duration_seconds] - Time limit for freeform exploration
 * @property {string} [freeform.objective] - What the student should discover
 * @property {boolean} [auto_advance] - Automatically advance after globe action completes (default false)
 * @property {number} [auto_advance_delay_ms] - Delay before auto-advancing (default 3000)
 * @property {string} [on_pass] - Branch target when the step's question resolves
 *   'correct' or 'partial': a step id, or 'next' (default) / 'end'. On a
 *   question-less step, acts as an unconditional goto. (CL-149, H2.3)
 * @property {string} [on_fail] - Branch target when the step's question resolves
 *   'incorrect' (incl. map_click attempts exhausted): a step id, or 'next'
 *   (default) / 'end'. 'review' verdicts NEVER branch — always 'next'. (CL-149)
 * @property {string[]} [concepts] - Concept ids for mastery tracking; overrides
 *   the lesson-level metadata.standards/tags fallback for this step. (CL-149)
 */

/**
 * Scoring configuration for the lesson.
 * @typedef {Object} Scoring
 * @property {number} [passing_score] - Minimum percentage to pass (0-100, default 60)
 * @property {boolean} [show_correct_answers] - Show correct answers after each question (default true)
 * @property {boolean} [allow_retry] - Allow retrying the lesson (default true)
 * @property {'per_question'|'end_only'} [feedback_mode] - When to show feedback (default 'per_question')
 */

/**
 * A complete lesson definition.
 * @typedef {Object} Lesson
 * @property {string} id - Unique lesson identifier (kebab-case)
 * @property {string} title - Display title
 * @property {string} description - Short description
 * @property {string} [version] - Semantic version (default '1.0.0')
 * @property {Object} metadata - Lesson metadata
 * @property {string} metadata.author - Author name
 * @property {string} metadata.grade_band - Grade range (e.g., '9-12')
 * @property {string} [metadata.subject] - Subject area (e.g., 'AP Human Geography')
 * @property {string[]} [metadata.standards] - Curriculum standard codes
 * @property {number} [metadata.duration_minutes] - Estimated duration
 * @property {'beginner'|'intermediate'|'advanced'} [metadata.difficulty] - Difficulty level
 * @property {string[]} [metadata.tags] - Searchable tags
 * @property {string[]} [required_datasets] - Dataset keys that must exist in catalog
 * @property {Step[]} steps - Ordered lesson steps
 * @property {Scoring} [scoring] - Scoring configuration
 */

/**
 * A lesson pack (collection of related lessons).
 * @typedef {Object} LessonPack
 * @property {string} id - Pack identifier
 * @property {string} title - Display title
 * @property {string} description - Pack description
 * @property {string} [program] - Curriculum program (e.g., 'AP Human Geography')
 * @property {string} [icon] - Emoji or icon identifier
 * @property {Object[]} units - Units containing lessons
 * @property {string} units[].unit_id - Unit identifier
 * @property {string} units[].title - Unit title
 * @property {Object[]} units[].lessons - Lessons in this unit
 * @property {string} units[].lessons[].id - Lesson ID
 * @property {string} units[].lessons[].title - Lesson title
 * @property {string} units[].lessons[].file - Filename relative to pack directory
 */

/**
 * The top-level lesson catalog.
 * @typedef {Object} LessonCatalog
 * @property {string} version - Catalog version
 * @property {Object[]} packs - Available lesson packs
 * @property {string} packs[].id - Pack ID
 * @property {string} packs[].title - Pack title
 * @property {string} packs[].description - Pack description
 * @property {string} packs[].program - Curriculum program
 * @property {string} packs[].pack_url - URL to pack.json (R2 or local)
 * @property {number} packs[].lesson_count - Number of lessons
 * @property {string} packs[].grade_band - Grade range
 * @property {'beginner'|'intermediate'|'advanced'} packs[].difficulty - Difficulty
 * @property {number} [packs[].duration_minutes] - Total estimated duration
 * @property {Object[]} [standalone] - Standalone lessons not in a pack
 */

// ── Validation ───────────────────────────────────────────────

const VALID_STEP_TYPES = [
  'guided',
  'freeform',
  'quiz',
  'narration',
  'checkpoint',
  'explore',
  'remediation',
]
const TRANSITION_KEYWORDS = ['next', 'end']
const VALID_QUESTION_TYPES = [
  'multiple_choice',
  'numeric',
  'open_response',
  'true_false',
  'fill_blank',
  'map_click',
]
const VALID_NARRATION_TIMING = ['before_action', 'after_action', 'with_action']
const VALID_DIFFICULTIES = ['beginner', 'intermediate', 'advanced']

/**
 * Validate a lesson JSON object against the schema, returning both blocking
 * errors and non-blocking authoring warnings (CL-149).
 *
 * @param {Object} json - The lesson JSON to validate
 * @returns {{ errors: string[], warnings: string[] }}
 */
export function validateLessonDetailed(json) {
  const errors = []
  const warnings = []

  if (!json || typeof json !== 'object') {
    return { errors: ['Lesson must be a non-null object'], warnings }
  }

  // Required top-level fields
  if (!json.id || typeof json.id !== 'string') {
    errors.push('Missing or invalid "id" (must be a non-empty string)')
  }
  if (!json.title || typeof json.title !== 'string') {
    errors.push('Missing or invalid "title" (must be a non-empty string)')
  }
  if (!json.description || typeof json.description !== 'string') {
    errors.push('Missing or invalid "description" (must be a non-empty string)')
  }

  // Metadata
  if (!json.metadata || typeof json.metadata !== 'object') {
    errors.push('Missing "metadata" object')
  } else {
    if (!json.metadata.author) errors.push('Missing "metadata.author"')
    if (!json.metadata.grade_band) errors.push('Missing "metadata.grade_band"')
    if (json.metadata.difficulty && !VALID_DIFFICULTIES.includes(json.metadata.difficulty)) {
      errors.push(
        `Invalid metadata.difficulty: "${json.metadata.difficulty}" (must be one of: ${VALID_DIFFICULTIES.join(', ')})`
      )
    }
  }

  // Steps
  if (!Array.isArray(json.steps) || json.steps.length === 0) {
    errors.push('Must have at least one step in "steps" array')
  } else {
    const stepIds = new Set(json.steps.map((s) => s?.id).filter(Boolean))

    json.steps.forEach((step, i) => {
      const prefix = `steps[${i}]`

      if (!step.id) errors.push(`${prefix}: missing "id"`)
      if (!step.title) errors.push(`${prefix}: missing "title"`)

      // Branch transitions (CL-149, roadmap H2.3)
      for (const field of ['on_pass', 'on_fail']) {
        const target = step[field]
        if (target === undefined) continue
        if (typeof target !== 'string' || !target.trim()) {
          errors.push(`${prefix}.${field}: must be a non-empty string (step id, "next", or "end")`)
          continue
        }
        if (TRANSITION_KEYWORDS.includes(target)) continue
        if (!stepIds.has(target)) {
          errors.push(`${prefix}.${field}: target "${target}" does not match any step id`)
        } else if (target === step.id) {
          errors.push(`${prefix}.${field}: must not target its own step ("${target}")`)
        }
      }
      if (step.on_fail !== undefined && !step.question) {
        warnings.push(`${prefix}: "on_fail" on a question-less step will never fire`)
      }

      // Concept tags (CL-149)
      if (step.concepts !== undefined) {
        if (
          !Array.isArray(step.concepts) ||
          step.concepts.some((c) => typeof c !== 'string' || !c.trim())
        ) {
          errors.push(`${prefix}.concepts: must be an array of non-empty strings`)
        }
      }

      if (!step.type || !VALID_STEP_TYPES.includes(step.type)) {
        errors.push(
          `${prefix}: invalid type "${step.type}" (must be one of: ${VALID_STEP_TYPES.join(', ')})`
        )
      }

      // Validate narration
      if (step.narration) {
        if (!step.narration.text || typeof step.narration.text !== 'string') {
          errors.push(`${prefix}.narration: missing or invalid "text"`)
        }
        if (step.narration.timing && !VALID_NARRATION_TIMING.includes(step.narration.timing)) {
          errors.push(`${prefix}.narration: invalid timing "${step.narration.timing}"`)
        }
      }

      // Validate globe_action
      if (step.globe_action) {
        if (!step.globe_action.command && !step.globe_action.intent) {
          errors.push(`${prefix}.globe_action: must have either "command" or "intent"`)
        }
      }

      // Validate question
      if (step.question) {
        const q = step.question
        if (!q.type || !VALID_QUESTION_TYPES.includes(q.type)) {
          errors.push(
            `${prefix}.question: invalid type "${q.type}" (must be one of: ${VALID_QUESTION_TYPES.join(', ')})`
          )
        }
        if (!q.prompt) {
          errors.push(`${prefix}.question: missing "prompt"`)
        }
        if (q.type === 'multiple_choice' && (!Array.isArray(q.choices) || q.choices.length < 2)) {
          errors.push(`${prefix}.question: multiple_choice requires at least 2 choices`)
        }
        if (q.type === 'multiple_choice' && q.correct_answer !== undefined) {
          if (!q.choices?.includes(q.correct_answer)) {
            errors.push(`${prefix}.question: correct_answer "${q.correct_answer}" not in choices`)
          }
        }
        if (q.type === 'numeric' && typeof q.correct_answer !== 'number') {
          errors.push(`${prefix}.question: numeric question requires a numeric correct_answer`)
        }
        if (
          q.type === 'true_false' &&
          !['true', 'false'].includes(String(q.correct_answer).toLowerCase())
        ) {
          errors.push(`${prefix}.question: true_false requires correct_answer of true or false`)
        }
        if (q.type === 'map_click') {
          const t = q.target
          if (!t || typeof t !== 'object') {
            errors.push(`${prefix}.question: map_click requires a "target" object ({ kind, name })`)
          } else {
            if (!['country', 'continent'].includes(t.kind)) {
              errors.push(
                `${prefix}.question: map_click target.kind must be "country" or "continent"`
              )
            }
            if (!t.name || typeof t.name !== 'string') {
              errors.push(`${prefix}.question: map_click target requires a non-empty "name" string`)
            }
            if (t.iso !== undefined && typeof t.iso !== 'string') {
              errors.push(`${prefix}.question: map_click target.iso must be a string`)
            }
          }
          if (q.attempts !== undefined && (!Number.isInteger(q.attempts) || q.attempts < 1)) {
            errors.push(`${prefix}.question: map_click attempts must be a positive integer`)
          }
        }
      }

      // Freeform steps need freeform config
      if (step.type === 'freeform' && !step.freeform) {
        errors.push(`${prefix}: freeform step requires "freeform" config object`)
      }
      if (step.freeform) {
        if (step.freeform.suggested_prompts && !Array.isArray(step.freeform.suggested_prompts)) {
          errors.push(`${prefix}.freeform: suggested_prompts must be an array`)
        }
      }
    })

    // Check for duplicate step IDs
    const ids = json.steps.map((s) => s.id).filter(Boolean)
    const dupes = ids.filter((id, i) => ids.indexOf(id) !== i)
    if (dupes.length > 0) {
      errors.push(`Duplicate step IDs: ${[...new Set(dupes)].join(', ')}`)
    }

    // ── Transition-graph checks (CL-149, warnings only) ──────
    // Unreachable remediation: a remediation step no transition targets is
    // almost certainly an authoring mistake (it is skipped by linear flow).
    const targeted = new Set()
    for (const s of json.steps) {
      for (const field of ['on_pass', 'on_fail']) {
        const t = s?.[field]
        if (typeof t === 'string' && !TRANSITION_KEYWORDS.includes(t)) targeted.add(t)
      }
    }
    for (const s of json.steps) {
      if (s?.type === 'remediation' && s.id && !targeted.has(s.id)) {
        warnings.push(
          `Remediation step "${s.id}" is not targeted by any on_pass/on_fail — it is unreachable`
        )
      }
    }

    // Cycle detection over explicit id-target edges. All cycles are flagged:
    // the engine's runtime visit budget always breaks them (fail-open), but a
    // cycle in authored JSON usually signals a routing mistake.
    const idIndex = new Map(json.steps.map((s, i) => [s?.id, i]))
    const adjacency = (i) => {
      const out = []
      for (const field of ['on_pass', 'on_fail']) {
        const t = json.steps[i]?.[field]
        if (typeof t === 'string' && idIndex.has(t)) out.push(idIndex.get(t))
      }
      return out
    }
    const color = new Array(json.steps.length).fill(0) // 0=white 1=gray 2=black
    const cycleAt = new Set()
    const dfs = (i) => {
      color[i] = 1
      for (const j of adjacency(i)) {
        if (color[j] === 1) cycleAt.add(json.steps[j]?.id)
        else if (color[j] === 0) dfs(j)
      }
      color[i] = 2
    }
    for (let i = 0; i < json.steps.length; i++) if (color[i] === 0) dfs(i)
    if (cycleAt.size > 0) {
      warnings.push(
        `Transition cycle detected involving step(s): ${[...cycleAt].join(', ')} — the runtime visit budget will break the loop, but check the routing`
      )
    }
  }

  // Scoring (optional but must be valid if present)
  if (json.scoring) {
    if (json.scoring.passing_score !== undefined) {
      if (
        typeof json.scoring.passing_score !== 'number' ||
        json.scoring.passing_score < 0 ||
        json.scoring.passing_score > 100
      ) {
        errors.push('scoring.passing_score must be a number between 0 and 100')
      }
    }
    if (
      json.scoring.feedback_mode &&
      !['per_question', 'end_only'].includes(json.scoring.feedback_mode)
    ) {
      errors.push(`scoring.feedback_mode must be "per_question" or "end_only"`)
    }
  }

  // CL-173 (roadmap H3.2b): optional persistent-stacking flag. 'stack' turns
  // on layer stack mode for the lesson's duration (G7/G8 overlay activities).
  if (json.layer_mode !== undefined && json.layer_mode !== 'stack') {
    errors.push(`layer_mode must be "stack" when present (got "${json.layer_mode}")`)
  }

  return { errors, warnings }
}

/**
 * Back-compat wrapper (pre-CL-149 shape): returns only the blocking errors.
 * Prefer validateLessonDetailed for new callers that can surface warnings.
 *
 * @param {Object} json - The lesson JSON to validate
 * @returns {string[]} Array of validation error messages
 */
export function validateLesson(json) {
  return validateLessonDetailed(json).errors
}

export default validateLesson
